import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';

const PRODUCTION_BASE_URL = 'https://www.selfstorage.com';

const createSizeSlug = (size) => {
	if (!size) return 'unit';
	// Convert "8' x 20'" to "8x20"
	return size.replace(/['\s]/g, '').toLowerCase();
};

const buildUnitSchema = (unit, facility, baseUrl) => {
	const rootUrl = baseUrl || PRODUCTION_BASE_URL;
	const size = unit.sizeText || unit.size || 'Storage Unit';
	const sizeSlug = createSizeSlug(size);
	const amenities = unit.availableAmenities?.length
		? unit.availableAmenities.join(', ')
		: 'secure storage';
	const price =
		unit.price?.sparefoot || unit.price?.regular || unit.price?.meta;
	const facilityUrl = `${rootUrl}${facility?.url || ''}`;

	return {
		'@context': 'https://schema.org',
		'@type': 'Product',
		name: `${size} Self Storage Unit at ${facility?.name || 'this facility'}`,
		description: `${size} self storage unit with ${amenities}. Reserve online safely and conveniently at SelfStorage.com.`,
		brand: {
			'@type': 'Brand',
			name: facility?.name || 'SelfStorage.com',
		},
		offers: {
			'@type': 'Offer',
			priceCurrency: 'USD',
			...(price != null && { price: price.toString() }),
			availability:
				unit.quantity === 0
					? 'https://schema.org/OutOfStock'
					: 'https://schema.org/InStock',
			url: `${facilityUrl}#${sizeSlug}`,
			inventoryLevel: {
				'@type': 'QuantitativeValue',
				value: unit.quantity ?? 1,
			},
		},
	};
};

export function UnitProductSchema({ units, facility, baseUrl }) {
	if (!units?.length || !facility) return null;

	const schemas = units
		.filter(Boolean)
		.map((unit) => buildUnitSchema(unit, facility, baseUrl));

	return (
		<Helmet>
			{schemas.map((schema, index) => (
				<script
					key={index}
					type="application/ld+json"
				>
					{JSON.stringify(schema).replace(/<\//g, '<\\/')}
				</script>
			))}
		</Helmet>
	);
}

UnitProductSchema.propTypes = {
	units: PropTypes.arrayOf(PropTypes.object),
	facility: PropTypes.object.isRequired,
	baseUrl: PropTypes.string,
};

export default UnitProductSchema;
